import { DoqType, DoqWithSideContent } from '@ardoq/doq';
import { ExternalLink } from '@ardoq/link';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { FlexBox } from '@ardoq/layout';
import { t, type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';
import { TertiaryButton } from '@ardoq/button';

type EmptyStateProps = {
  hasSearchResults?: boolean;
  term: string;
  selectedComponentTypeHasNoInstances: boolean;
  selectedType: string;
  i18nLocale: I18nLocale;
};

export const ComponentSearchEmptyState = ({
  hasSearchResults,
  term,
  selectedComponentTypeHasNoInstances,
  selectedType,
  i18nLocale,
}: EmptyStateProps) => {
  return (
    <FlexBox justify="center" align="center" flex={1}>
      {selectedComponentTypeHasNoInstances && (
        <NoInstancesForType
          selectedType={selectedType}
          i18nLocale={i18nLocale}
        />
      )}
      {!selectedComponentTypeHasNoInstances && hasSearchResults && (
        <NoResult term={term} i18nLocale={i18nLocale} />
      )}
      {!hasSearchResults && <HowToUseGuide i18nLocale={i18nLocale} />}
    </FlexBox>
  );
};

export const ViewpointFiltersEmptyState = ({
  onEditViewpoint,
  i18nLocale,
}: {
  onEditViewpoint: VoidFunction;
  i18nLocale: I18nLocale;
}) => {
  return (
    <FlexBox justify="center" align="center" flex={1}>
      <DoqWithSideContent
        doqType={DoqType.EMPTY_BOX}
        title={t(
          i18nLocale,
          'viewpointBuilder:selectContext.viewpointFilters.title',
          "No components match this Viewpoint's filters"
        )}
        buttons={
          <TertiaryButton onClick={onEditViewpoint}>
            {t(
              i18nLocale,
              'viewpointBuilder:selectContext.viewpointFilters.editViewpoint',
              'Edit Viewpoint'
            )}
          </TertiaryButton>
        }
      >
        <p>
          {t(
            i18nLocale,
            'viewpointBuilder:selectContext.viewpointFilters.body',
            'Edit this Viewpoint to change its filters or use a different Viewpoint.'
          )}
        </p>
      </DoqWithSideContent>
    </FlexBox>
  );
};

const HowToUseGuide = ({ i18nLocale }: WithI18nLocale) => {
  return (
    <DoqWithSideContent
      doqType={DoqType.WAITING}
      title={t(
        i18nLocale,
        'viewpointBuilder:selectContext.emptyState.howItWorks.title',
        'How this works'
      )}
    >
      <p>
        {t(
          i18nLocale,
          'viewpointBuilder:selectContext.emptyState.howItWorks.intro',
          'First select one or more context components. Next, you can'
        )}
      </p>
      <ul>
        <li>
          {t(
            i18nLocale,
            'viewpointBuilder:selectContext.emptyState.howItWorks.option1',
            'Open just the selected components without opening the entire workspace or'
          )}
        </li>
        <li>
          {t(
            i18nLocale,
            'viewpointBuilder:selectContext.emptyState.howItWorks.option2',
            'Select references to see other component types linked to the context component'
          )}
        </li>
        <li>
          {t(
            i18nLocale,
            'viewpointBuilder:selectContext.emptyState.howItWorks.option3',
            'Add filters to narrow down the components in your dataset'
          )}
        </li>
      </ul>
      <ExternalLink
        opensInNewTabLabel={t(
          i18nLocale,
          'common:labels.opensInNewTab',
          '(new tab)'
        )}
        href={KnowledgeBaseLink.VIEWPOINT_BUILDER_PHOENIX}
      >
        {t(
          i18nLocale,
          'viewpointBuilder:selectContext.emptyState.howItWorks.learnMore',
          'Learn more about the new Ardoq Viewpoints'
        )}
      </ExternalLink>
    </DoqWithSideContent>
  );
};

const NoResult = ({
  term,
  i18nLocale,
}: {
  term: string;
  i18nLocale: I18nLocale;
}) => {
  return (
    <DoqWithSideContent
      doqType={DoqType.MISSING_ORG}
      title={t(
        i18nLocale,
        'viewpointBuilder:selectContext.emptyState.noResults.title',
        'No results found'
      )}
    >
      <p>
        {t(
          i18nLocale,
          'viewpointBuilder:selectContext.emptyState.noResults.body',
          { term },
          'There is no component with the name "{{term}}". Try searching for a different component or change your component type filter.'
        )}
      </p>
    </DoqWithSideContent>
  );
};

const NoInstancesForType = ({
  selectedType,
  i18nLocale,
}: {
  selectedType: string;
  i18nLocale: I18nLocale;
}) => {
  return (
    <DoqWithSideContent
      doqType={DoqType.MISSING_ORG}
      title={t(
        i18nLocale,
        'viewpointBuilder:selectContext.emptyState.noInstances.title',
        'No components available'
      )}
    >
      <p>
        {t(
          i18nLocale,
          'viewpointBuilder:selectContext.emptyState.noInstances.body',
          { selectedType },
          'There are no components of type "{{selectedType}}". Try searching for a different component type.'
        )}
      </p>
    </DoqWithSideContent>
  );
};
